// The program's names for the ids a contract-model row carries: which
// deliverable, which team, which period, which optional task and exercise.
// One hook so every screen and drawer reads "Box Admin & Cloud" for the same
// workstream id, and nothing ever renders a raw id.
import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { authClient, projectDataClient } from '../../../lib/connect/clients';
import { contractDateISO } from '../../../lib/contractDate';
import { isOpenReviewItem } from '../_shared/components/review/reviewItemCopy';
import { isSowSilent, sowOfItem } from '../_shared/components/review/reviewItemGroups';
import { optionalTaskHooks, popHooks, useOptionalTaskExercisesForProgram } from '../Programs/operationalHooks';
import { programContactHooks } from '../Programs/relationshipHooks';
import { useProgramModificationChangeSets } from '../Programs/v2/modificationHooks';
import { useDuties, useWorkstreams } from './hooks';
import { parseAlongside } from './lib/rule';
export function useCmLookups(programId) {
    const duties = useDuties(programId, {
        includeRules: true
    });
    const workstreams = useWorkstreams(programId);
    const pops = popHooks.useList({
        programId
    });
    const ots = optionalTaskHooks.useList({
        programId
    });
    const exercises = useOptionalTaskExercisesForProgram(programId);
    const contacts = programContactHooks.useList({
        programId
    });
    return useMemo(()=>{
        const dutyList = duties.data?.duties ?? [];
        const ruleList = duties.data?.rules ?? [];
        const rulesByDuty = new Map();
        for (const r of ruleList){
            const arr = rulesByDuty.get(r.dutyId) ?? [];
            arr.push(r);
            rulesByDuty.set(r.dutyId, arr);
        }
        const checkCodes = new Map();
        for (const [k, v] of Object.entries(duties.data?.checkCodes ?? {}))checkCodes.set(k, v.codes);
        const undated = new Map();
        for (const u of duties.data?.undatedReasons ?? [])undated.set(`${u.dutyId}:${u.assignmentId}`, u);
        const routingsByDuty = new Map();
        for (const r of duties.data?.routings ?? []){
            const arr = routingsByDuty.get(r.dutyId) ?? [];
            arr.push(r);
            routingsByDuty.set(r.dutyId, arr);
        }
        const wsList = workstreams.data ?? [];
        const wsById = new Map(wsList.map((w)=>[
                w.id,
                w
            ]));
        const periods = (pops.data?.items ?? []).map((p)=>({
                id: p.id,
                name: p.periodName || (p.optionNumber ? `Option ${p.optionNumber}` : 'Base'),
                optionNumber: p.optionNumber ?? 0,
                start: contractDateISO(p.startDate),
                end: contractDateISO(p.endDate),
                sortOrder: p.sortOrder,
                exercised: p.exercised
            })).sort((a, b)=>(a.start || '').localeCompare(b.start || '') || a.sortOrder - b.sortOrder);
        const otList = (ots.data?.items ?? []).map((t)=>({
                id: t.id,
                name: t.name,
                number: t.taskNumber,
                short: t.taskNumber ? `OT${t.taskNumber}` : t.name,
                clinNumber: t.clinNumber ?? ''
            }));
        const exList = (exercises.data ?? []).map((e)=>({
                id: e.id,
                optionalTaskId: e.optionalTaskId,
                clinNumber: e.clinNumber,
                start: contractDateISO(e.popStartDate),
                end: contractDateISO(e.popEndDate),
                lines: (e.contributingSlins ?? []).map((s)=>({
                        clinNumber: s.clinNumber,
                        description: s.description
                    })).sort((a, b)=>a.clinNumber.localeCompare(b.clinNumber, undefined, {
                        numeric: true
                    }))
            }));
        const contactList = (contacts.data?.items ?? []).map((c)=>({
                id: c.contactId || c.id,
                name: c.contactName || c.contactEmail || 'Unnamed contact',
                email: c.contactEmail
            }));
        return {
            isLoading: duties.isLoading || workstreams.isLoading,
            duties: dutyList,
            dutyById: new Map(dutyList.map((d)=>[
                    d.id,
                    d
                ])),
            rules: ruleList,
            rulesByDuty,
            checkCodes,
            undatedReasons: duties.data?.undatedReasons ?? [],
            undatedReason: (dutyId, assignmentId = '')=>(assignmentId ? undated.get(`${dutyId}:${assignmentId}`) : undefined) ?? undated.get(`${dutyId}:`),
            routingsByDuty,
            watches: duties.data?.watches ?? [],
            workstreams: wsList,
            workstreamById: wsById,
            periods,
            periodById: new Map(periods.map((p)=>[
                    p.id,
                    p
                ])),
            optionalTasks: otList,
            optionalTaskById: new Map(otList.map((t)=>[
                    t.id,
                    t
                ])),
            exercises: exList,
            exerciseById: new Map(exList.map((e)=>[
                    e.id,
                    e
                ])),
            contacts: contactList,
            teamName: (id)=>id ? wsById.get(id)?.name ?? 'Unknown team' : 'Contract-wide',
            rulePlace: (r)=>{
                // SAD-861: a rule due alongside another deliverable applies wherever that one is due.
                if (parseAlongside(r.eventKind)) return {
                    label: 'every period the other deliverable is due',
                    order: 5000,
                    kind: 'other'
                };
                const period = periods.find((p)=>p.id === r.periodOfPerformanceId);
                if (r.optionalTaskId) {
                    const ot = otList.find((t)=>t.id === r.optionalTaskId);
                    return {
                        label: ot?.short ?? 'Optional task',
                        order: 1000 + (ot?.number ?? 0),
                        n: ot?.number,
                        kind: 'ot'
                    };
                }
                if (r.gate === 'base' || period && !period.optionNumber) return {
                    label: 'Base',
                    order: 0,
                    kind: 'base'
                };
                if (period) return {
                    label: `OP${period.optionNumber}`,
                    order: period.optionNumber,
                    n: period.optionNumber,
                    kind: 'op'
                };
                return {
                    label: r.assignmentId ? 'Team rule' : 'Contract',
                    order: 5000,
                    kind: 'other'
                };
            }
        };
    }, [
        duties.data,
        duties.isLoading,
        workstreams.data,
        workstreams.isLoading,
        pops.data,
        ots.data,
        exercises.data,
        contacts.data
    ]);
}
/**
 * Tenant users' names by id, for "Waiver proposed by Ana on 3 Mar". A
 * proposal carries only the user id; the sharing directory is readable by
 * every signed-in user (unlike the admin people list). The viewer reads as
 * "you".
 */ export function useUserNames() {
    const { user } = useAuth();
    const q = useQuery({
        queryKey: [
            'cm-user-names'
        ],
        staleTime: 5 * 60000,
        queryFn: async ()=>(await authClient.listUsersForSharing({
                query: ''
            })).users
    });
    return useCallback((userId)=>{
        if (!userId) return 'someone';
        if (user?.id && userId === user.id) return 'you';
        const u = q.data?.find((x)=>x.id === userId);
        return u?.name || u?.email || 'a teammate';
    }, [
        q.data,
        user?.id
    ]);
}
const CONDITION_TARGET = 'DutyCondition';
/** The intake question, opened (Docs → Modifications). */ function intakeItemHref(programId, changeSetId, itemId) {
    return `/delivery/programs/${programId}/docs?tab=modifications&set=${encodeURIComponent(changeSetId)}&item=${encodeURIComponent(itemId)}`;
}
/** The program's open intake sets and their items, read only when `enabled`. */ function useOpenIntakeItems(programId, enabled) {
    const { user } = useAuth();
    const sets = useProgramModificationChangeSets(enabled ? programId : '', user?.tenant_id ?? '');
    const intake = (sets.data ?? []).filter((cs)=>cs.changeSetType === 'ContractIntake' && !CLOSED_SETS.has(cs.state));
    const items = useQueries({
        queries: intake.map((cs)=>({
                queryKey: [
                    'change-set-discrepancies',
                    cs.changeSetId
                ],
                queryFn: async ()=>{
                    const resp = await projectDataClient.listChangeSetDiscrepancies({
                        changeSetId: cs.changeSetId
                    });
                    if (!resp.summary) throw new Error('ListChangeSetDiscrepancies returned no summary');
                    return resp.summary;
                }
            }))
    });
    return {
        intake,
        items
    };
}
const CLOSED_SETS = new Set([
    'Applied',
    'Cancelled',
    'Rejected',
    'Superseded'
]);
/**
 * SAD-851: for a deliverable held by an unresolved condition set
 * (condition_not_settled), the question that holds it: the open intake item
 * whose branches include the deliverable's PWS section or a parent of it.
 * Reads the program's open intake sets only when `enabled`.
 */ export function useConditionHolds(programId, enabled) {
    const { intake, items } = useOpenIntakeItems(programId, enabled);
    const byBranch = new Map();
    items.forEach((q, i)=>{
        for (const d of q.data?.discrepancies ?? []){
            const r = d.review;
            if (!r || r.targetObjectType !== CONDITION_TARGET || !isOpenReviewItem(d)) continue;
            const href = intakeItemHref(programId, intake[i].changeSetId, d.id);
            for (const c of r.candidates)if (c.key) byBranch.set(c.key, {
                section: r.targetLocator,
                href
            });
        }
    });
    return (pwsRef)=>{
        for(let ref = pwsRef.trim(); ref; ref = ref.includes('.') ? ref.slice(0, ref.lastIndexOf('.')) : ''){
            const hold = byBranch.get(ref);
            if (hold) return hold;
        }
        return undefined;
    };
}
/**
 * SAD-969: the open "SOW doesn't mention this" questions, by deliverable id.
 * Until answered the deliverable is owed per the award schedule; the row links
 * here so the lazy path to answering is one click.
 */ export function useSowSilentQuestions(programId, enabled = true) {
    const { intake, items } = useOpenIntakeItems(programId, enabled);
    const byDuty = new Map();
    items.forEach((q, i)=>{
        for (const d of q.data?.discrepancies ?? []){
            const r = d.review;
            if (!r || !isSowSilent(r) || !isOpenReviewItem(d)) continue;
            const dutyId = silentDutyId(r);
            if (!dutyId) continue;
            byDuty.set(dutyId, [
                ...byDuty.get(dutyId) ?? [],
                {
                    sowId: sowOfItem(d),
                    href: intakeItemHref(programId, intake[i].changeSetId, d.id)
                }
            ]);
        }
    });
    return (dutyId)=>byDuty.get(dutyId) ?? [];
}
/** The deliverable a silent item answers for: its candidates' duty_id. */ function silentDutyId(r) {
    for (const c of r.candidates){
        try {
            const v = JSON.parse(c.valueJson);
            if (v.duty_id) return v.duty_id;
        } catch  {
        /* not an assignment */ }
    }
    return '';
}
